import React, { useEffect, useState } from "react";
import { adminApi } from "./api";
import {
  Users, Sparkles, CalendarDays, Download, DollarSign,
  Eye, TrendingUp, TrendingDown, MousePointerClick,
} from "lucide-react";

const fmt = (n) =>
  typeof n === "number" ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : (n || "—");
const fmtAUD = (n) =>
  `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} AUD`;
const pct = (curr, prev) => {
  if (!prev) return curr > 0 ? 100 : 0;
  return ((curr - prev) / prev) * 100;
};

const Delta = ({ curr, prev }) => {
  const p = pct(curr, prev);
  const positive = p >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${positive ? "text-emerald-600" : "text-rose-600"}`}>
      <Icon className="w-3 h-3" />
      {Math.abs(p).toFixed(0)}%
    </span>
  );
};

const KPI = ({ label, value, hint, icon: Icon, accent, delta }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-sm transition-shadow">
    <div className="flex items-center justify-between">
      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</p>
      <span className={`w-8 h-8 rounded-full flex items-center justify-center ${accent}`}>
        <Icon className="w-4 h-4" />
      </span>
    </div>
    <p className="mt-3 font-serif text-3xl text-slate-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
      {value}
    </p>
    <div className="mt-1 flex items-center gap-2">
      {delta}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  </div>
);

function TrafficChart({ data }) {
  if (!data?.length) {
    return (
      <div className="h-48 flex items-center justify-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-xl">
        No traffic recorded yet — visit the site to seed data.
      </div>
    );
  }
  const max = Math.max(...data.map((d) => d.pageviews), 1);
  const w = 1000, h = 220, pad = 24;
  const bw = (w - pad * 2) / data.length;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-56">
      <defs>
        <linearGradient id="bar-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#0F172A" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#0F172A" stopOpacity="0.55" />
        </linearGradient>
      </defs>
      {data.map((d, i) => {
        const bh = (d.pageviews / max) * (h - pad * 2);
        const vh = (d.visitors / max) * (h - pad * 2);
        const x = pad + i * bw;
        return (
          <g key={d.day}>
            <rect x={x + bw * 0.18} y={h - pad - bh} width={bw * 0.64} height={bh} fill="url(#bar-grad)" rx="3" />
            <rect x={x + bw * 0.18} y={h - pad - vh} width={bw * 0.64} height={3} fill="#D4AF37" rx="2" />
          </g>
        );
      })}
      {/* x-axis labels (sparse) */}
      {data.map((d, i) => {
        if (i % Math.ceil(data.length / 6) !== 0) return null;
        return (
          <text key={`l-${d.day}`} x={pad + i * bw + bw / 2} y={h - 6}
                fontSize="9" fill="#94A3B8" textAnchor="middle">
            {d.day.slice(5)}
          </text>
        );
      })}
    </svg>
  );
}

const PRETTY_EVENT = {
  blueprint_started: "Blueprint form opened",
  blueprint_generated: "Blueprint generated",
  unlock_clicked: "Unlock CTA clicked",
  unlock_paid: "Unlock paid",
  pdf_downloaded: "PDF downloaded",
  cta_hero_clicked: "Hero CTA clicked",
  service_clicked: "Service card clicked",
  booking_opened: "Booking modal opened",
  booking_submitted: "Booking submitted",
  navbar_blueprint_clicked: "Nav · Blueprint",
  navbar_sessions_clicked: "Nav · Sessions",
};

const FunnelStep = ({ step, count, maxCount, drop }) => {
  const width = maxCount ? (count / maxCount) * 100 : 0;
  return (
    <div data-testid={`funnel-step-${step.step}`}>
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-700">{PRETTY_EVENT[step.step] || step.step}</span>
        <span className="text-slate-900 font-medium tabular-nums">
          {fmt(count)}
          {drop !== null && <span className="ml-2 text-xs text-rose-600">−{drop.toFixed(0)}%</span>}
        </span>
      </div>
      <div className="mt-1.5 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full transition-all"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
};

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [traffic, setTraffic] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      adminApi.get("/stats"),
      adminApi.get("/traffic?days=30"),
      adminApi.get("/analytics?days=30"),
    ])
      .then(([s, t, a]) => { setStats(s.data); setTraffic(t.data); setAnalytics(a.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-12 text-slate-500">Loading…</div>;

  const unlockRate = stats.profiles_total
    ? (stats.profiles_unlocked / stats.profiles_total) * 100
    : 0;
  const maxFunnel = Math.max(...(analytics?.funnel || []).map((f) => f.visitors), 1);

  return (
    <div className="p-10 max-w-7xl mx-auto space-y-10" data-testid="admin-overview">
      <header>
        <p className="text-[10px] uppercase tracking-[0.22em] text-amber-700">overview</p>
        <h1 className="font-serif text-4xl text-slate-900 mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Welcome back, <span className="italic text-amber-700">Kassie</span>.
        </h1>
        <p className="mt-2 text-sm text-slate-500">Live performance across the last 30 days · vs previous 30 days.</p>
      </header>

      {/* KPI grid */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
        <KPI label="Pageviews" value={fmt(stats.pageviews_30d)}
             delta={<Delta curr={stats.pageviews_30d} prev={stats.pageviews_prev_30d} />}
             hint="vs prev 30d" icon={Eye} accent="bg-sky-50 text-sky-700" />
        <KPI label="Unique visitors" value={fmt(stats.unique_visitors_30d)}
             delta={<Delta curr={stats.unique_visitors_30d} prev={stats.unique_visitors_prev_30d} />}
             hint="vs prev 30d" icon={Users} accent="bg-indigo-50 text-indigo-700" />
        <KPI label="Blueprints generated" value={fmt(stats.profiles_total)}
             hint={`${unlockRate.toFixed(0)}% unlock rate`} icon={Sparkles} accent="bg-amber-50 text-amber-700" />
        <KPI label="Sessions booked" value={fmt(stats.bookings_total)}
             hint="paid &amp; confirmed" icon={CalendarDays} accent="bg-emerald-50 text-emerald-700" />
      </section>

      {/* Revenue card with split */}
      <section className="bg-white rounded-2xl border border-slate-200 p-7" data-testid="revenue-split">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">revenue</p>
            <h2 className="font-serif text-2xl text-slate-900 mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {fmtAUD(stats.revenue_total)}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              {fmtAUD(stats.revenue_unlocks)} blueprints · {fmtAUD(stats.revenue_sessions)} sessions
            </p>
          </div>
          <DollarSign className="w-8 h-8 text-slate-300" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-700">Kama Loka Journeys (Kassie) · 90%</p>
            <p className="mt-1 font-serif text-2xl text-emerald-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {fmtAUD(stats.client_net_aud)}
            </p>
            <p className="text-[11px] text-emerald-700/70 mt-0.5">from blueprint unlocks</p>
          </div>
          <div className="bg-violet-50 border border-violet-100 rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-[0.22em] text-violet-700">Borntobealive · {stats.platform_fee_pct}%</p>
            <p className="mt-1 font-serif text-2xl text-violet-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {fmtAUD(stats.platform_fee_aud)}
            </p>
            <p className="text-[11px] text-violet-700/70 mt-0.5">LLM &amp; infrastructure fees</p>
          </div>
        </div>
      </section>

      {/* Traffic chart */}
      <section className="bg-white rounded-2xl border border-slate-200 p-7">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">traffic</p>
            <h2 className="font-serif text-2xl text-slate-900 mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Last 30 days
            </h2>
          </div>
          <div className="flex items-center gap-5 text-xs">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-900" /> Pageviews</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-500" /> Unique visitors</span>
          </div>
        </div>
        <TrafficChart data={traffic} />
      </section>

      {/* Funnel + Top events */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-7" data-testid="funnel-card">
          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">funnel</p>
          <h2 className="font-serif text-2xl text-slate-900 mt-1 mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Visitor journey
          </h2>
          <div className="space-y-4">
            {(analytics?.funnel || []).map((f, i, arr) => {
              const prev = i > 0 ? arr[i - 1].visitors : null;
              const drop = prev && prev > 0 ? ((prev - f.visitors) / prev) * 100 : null;
              return <FunnelStep key={f.step} step={f} count={f.visitors} maxCount={maxFunnel} drop={i === 0 ? null : drop} />;
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-7" data-testid="top-events-card">
          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">engagement</p>
          <h2 className="font-serif text-2xl text-slate-900 mt-1 mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Top events
          </h2>
          {(analytics?.top_events || []).length === 0 ? (
            <p className="text-sm text-slate-400 flex items-center gap-2"><MousePointerClick className="w-4 h-4" /> No clicks tracked yet.</p>
          ) : (
            <ul className="space-y-3">
              {(() => {
                const maxCount = Math.max(...analytics.top_events.map((e) => e.count), 1);
                return analytics.top_events.map((e) => {
                  const w = (e.count / maxCount) * 100;
                  return (
                    <li key={e.name}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">{PRETTY_EVENT[e.name] || e.name}</span>
                        <span className="font-medium text-slate-900 tabular-nums">{fmt(e.count)}</span>
                      </div>
                      <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-slate-900/80 rounded-full"
                          style={{ width: `${w}%` }}
                        />
                      </div>
                    </li>
                  );
                });
              })()}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
